import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout, { AdminBanner } from '../AdminLayout/AdminLayout';
import './CreateUser.css';
import { createUserUserMasterKey } from '../../../services/api';
import { Role, settableRoleOptions } from '../../../model/Roles';
import { useMessages } from '../../../i18n/I18n';
import { useDialogs } from '../../../components/Dialogs';

const CreateUser: React.FC = () => {
  const t = useMessages('admin');
  const roleMessages = useMessages('roles');
  const apiMessages = useMessages('api');
  const [masterKey, setMasterKey] = useState('');
  const [festivalId, setFestivalId] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  const { alert } = useDialogs();

  const handleRoleToggle = (role: Role) => {
    setSelectedRoles(prevRoles => {
      if (prevRoles.includes(role)) {
        return prevRoles.filter(r => r !== role);
      } else {
        return [...prevRoles, role];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await createUserUserMasterKey(masterKey, festivalId, email, selectedRoles);
      await alert(t.userCreated, { title: t.userCreatedTitle });
      history.push('/admin');
    } catch (err) {
      if (err instanceof Error) {
        // The API's errors are already sentences; anything else (the network) gets the usual prefix
        const isSentence = err.message === apiMessages.wrongMasterKey || err.message.includes(apiMessages.createUserFailedPrefix);
        setError(isSentence ? err.message : apiMessages.createUserFailed(err.message));
      } else {
        setError(t.error);
      }
    }
  };

  return (
    <AdminLayout
      className="create-user-page"
      title={t.tools.createUser.title}
      description={t.createUserDescription}
      hasBackLink
    >
      <section className="admin-card">
        {error && <AdminBanner tone="danger">{error}</AdminBanner>}
        <form onSubmit={handleSubmit}>
          <div className="admin-field">
            <label htmlFor="masterKey">{t.masterKey}</label>
            <input
              type="password"
              id="masterKey"
              value={masterKey}
              onChange={(e) => setMasterKey(e.target.value)}
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="festivalId">{t.festivalId}</label>
            <input
              type="text"
              id="festivalId"
              value={festivalId}
              onChange={(e) => setFestivalId(e.target.value)}
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <fieldset className="admin-field">
            <legend>{t.roles}</legend>
            <div className="roles-selection">
              {settableRoleOptions(roleMessages).map(({ value, label, description }) => (
                <label key={value} className="role-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(value)}
                    onChange={() => handleRoleToggle(value)}
                  />
                  <span className="role-name">{label}</span>
                  <span className="role-description">{description}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="admin-actions">
            <button type="submit" className="btn btn-primary">{t.createUser}</button>
          </div>
        </form>
      </section>
    </AdminLayout>
  );
};

export default CreateUser; 